import { useEffect, useRef, useState, type FormEvent, type ReactNode, type RefObject } from "react";
import { ChevronRight, ImagePlus, X } from "lucide-react";
import { useKeepInView } from "../hooks/useKeepInView";
import { LaneNote } from "./LaneNote";

export interface PhotoActionEntry {
  id: string;
  label: string;
  src: string;
}

interface PhotoActionsProps {
  wide: boolean;
  top: number;
  left: number;
  recordId?: string;
  /** Replace saves straight away: the photo is bound, or it's an image the design didn't bind. */
  replaceable?: boolean;
  /** An empty box for an item's photo: the only thing to do is add one (or ask Stax). */
  empty?: boolean;
  alt?: string;
  src: string;
  pageTitle: string;
  photos: PhotoActionEntry[];
  replacing: boolean;
  busy?: boolean;
  error?: string | null;
  sharedCount?: number;
  onClose: () => void;
  onReplace: () => void;
  onDescribe: (alt: string) => void;
  onSelectPhoto: (photo: PhotoActionEntry) => void;
  onUpload: (files: File[]) => void;
  onCrop: () => void;
  onComment: () => void;
  children?: ReactNode;
  /** Point to change: where the photo sits, with the levels above selectable instead. */
  trail?: ReactNode;
}

export function PhotoActions({
  wide, top, left, recordId, replaceable = Boolean(recordId), empty = false, alt = "", src, pageTitle, photos, replacing, busy = false, error, sharedCount, onClose, onReplace, onDescribe, onSelectPhoto, onUpload, onCrop, onComment, children, trail,
}: PhotoActionsProps) {
  const dialog = useRef<HTMLDivElement>(null);
  const uploadInput = useRef<HTMLInputElement>(null);
  const [description, setDescription] = useState(alt);
  const [describing, setDescribing] = useState(false);
  const [position, setPosition] = useState({ top, left, tether: "below" as "below" | "above" | "none" });
  const view = useKeepInView(dialog, `${position.top}:${replacing}:${describing}`);
  // Point to change opens straight onto your photos, first thing under what was selected.
  const picker = replacing ? <PhotoPicker replaceable={replaceable} photos={photos} busy={busy} empty={empty} uploadInput={uploadInput} onSelectPhoto={onSelectPhoto} onUpload={onUpload}>{children}</PhotoPicker> : null;
  useEffect(() => {
    const firstAction = dialog.current?.querySelector<HTMLElement>("[data-photo-action-first]");
    // With nothing to focus first (Point to change opens on the photos), the dialog itself takes focus so Escape closes it.
    (firstAction ?? dialog.current)?.focus({ preventScroll: true });
  }, []);
  useEffect(() => setDescription(alt), [alt]);
  useEffect(() => {
    if (!wide || !dialog.current) return;
    const frame = dialog.current.closest<HTMLElement>(".bw-frame");
    if (!frame) return;
    const frameBox = frame.getBoundingClientRect();
    const dialogBox = dialog.current.getBoundingClientRect();
    let nextLeft = left;
    let nextTop = top;
    let tether: "below" | "above" | "none" = "below";
    if (nextLeft + dialogBox.width > frameBox.width - 8) {
      nextLeft = Math.max(8, left - dialogBox.width - 16);
      tether = "none";
    }
    if (nextTop + dialogBox.height > frameBox.height - 8) {
      nextTop = Math.max(8, top - dialogBox.height - 16);
      if (tether === "below") tether = "above";
    }
    setPosition({ top: nextTop, left: nextLeft, tether });
  }, [left, top, wide, replacing, children]);
  return (
    <div
      ref={dialog}
      className="bw-text-editor bw-photo-actions"
      role="dialog"
      tabIndex={-1}
      aria-modal={!wide}
      aria-label="This photo"
      data-tether={position.tether}
      style={{ top: position.top, left: position.left, transform: view.transform(), ...view.style }}
      onKeyDown={(event) => {
        if (event.key === "Escape") {
          event.stopPropagation();
          onClose();
        }
        if (event.key !== "Tab" || wide) return;
        const controls = Array.from(event.currentTarget.querySelectorAll<HTMLElement>('button:not(:disabled), input:not([type="file"]), textarea'))
          .filter((element) => element.offsetParent !== null);
        const first = controls[0];
        const last = controls.at(-1);
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last?.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first?.focus();
        }
      }}
    >
      <div className="bw-photo-head">
        {empty ? <span className="bw-photo-empty" aria-hidden="true"><ImagePlus size={20} /></span> : <img src={src} alt="" />}
        {trail ? <div>{trail}</div> : <div><strong>{empty ? "Add a photo" : "This photo"}</strong><span>{pageTitle}</span></div>}
        <button type="button" className="bw-btn bw-btn--icon" onClick={onClose} aria-label="Close photo actions"><X size={18} aria-hidden="true" /></button>
      </div>
      {!empty && sharedCount && sharedCount > 1 ? <p className="bw-photo-note">This photo shows in {sharedCount} places. Changes apply to all of them.</p> : null}
      {trail && picker}
      {!empty && !trail && <button type="button" className="bw-photo-action" data-photo-action-first disabled={busy} onClick={onReplace}><span><strong>Replace photo</strong><small>From your photos or upload a new one</small></span><ChevronRight size={18} aria-hidden="true" /></button>}
      {!empty && <button type="button" className="bw-photo-action" onClick={() => recordId ? setDescribing((value) => !value) : onDescribe("")}><span><strong>Describe it</strong><small>For people using screen readers</small></span><ChevronRight size={18} aria-hidden="true" /></button>}
      {recordId && describing ? (
        <form onSubmit={(event: FormEvent) => {
          event.preventDefault();
          onDescribe(description);
        }}>
          <label><span className="bw-sr">Photo description</span><textarea value={description} onChange={(event) => setDescription(event.target.value)} /></label>
          <button type="submit" className="bw-btn bw-btn--quiet">Save description</button>
        </form>
      ) : null}
      {!empty && <button type="button" className="bw-photo-action" disabled={busy} onClick={onCrop}><span><strong>Crop</strong><small>Choose which part shows</small></span><ChevronRight size={18} aria-hidden="true" /></button>}
      <button type="button" className="bw-photo-action" disabled={busy} onClick={onComment}><span><strong>Comment for Stax</strong><small>{empty ? "e.g. “find a photo that fits this”" : "e.g. “use a brighter photo here”"}</small></span><ChevronRight size={18} aria-hidden="true" /></button>
      {error ? <p className="bw-photo-note" role="alert">{error}</p> : null}
      {empty
        ? <LaneNote lane="instant">Adding a photo saves straight away and can be undone. Comment asks Stax.</LaneNote>
        : recordId
        ? <LaneNote lane="instant">Photo changes save straight away and can be undone. Comment asks Stax.</LaneNote>
        : replaceable
          ? <LaneNote lane="instant">Replacing it saves straight away and can be undone. Other changes go to Stax.</LaneNote>
          : <LaneNote lane="requested">This photo is part of the design, so Stax changes it when you send your comments.</LaneNote>}
      {!trail && picker}
    </div>
  );
}

function PhotoPicker({ replaceable, photos, busy, empty, uploadInput, onSelectPhoto, onUpload, children }: Pick<PhotoActionsProps, "replaceable" | "photos" | "busy" | "empty" | "onSelectPhoto" | "onUpload" | "children"> & { uploadInput: RefObject<HTMLInputElement | null> }) {
  return (
        <>
          {replaceable ? null : <p>Stax will swap it when you send your comments.</p>}
          <div className="bw-photo-grid">
            {photos.map((photo) => (
              <button key={photo.id} type="button" className="bw-photo-option" disabled={busy} onClick={() => onSelectPhoto(photo)}>
                <img src={photo.src} alt="" />
                <span>{photo.label}</span>
              </button>
            ))}
          </div>
          <input
            ref={uploadInput}
            className="bw-sr"
            type="file"
            accept="image/*"
            multiple
            onChange={(event) => {
              if (event.target.files?.length) onUpload([...event.target.files]);
            }}
          />
          {children}
          <button type="button" className="bw-btn bw-btn--quiet" data-photo-action-first={empty || undefined} disabled={busy} onClick={() => uploadInput.current?.click()}>
            {busy ? "Saving…" : empty ? "Upload a photo" : "Upload a new one"}
          </button>
        </>
  );
}
